import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Printer } from '../domain/printer/printer';
import { PrinterService } from '../domain/printer/printer.service';
import { PrinterClass } from '../domain/printerClass/printer-class';
import { PrinterClassService } from '../domain/printerClass/printer-class.service';
import { AddPrinterDialogComponent } from './add-printer-dialog/add-printer-dialog.component';
import { DeletePrinterDialogComponent } from './delete-printer-dialog/delete-printer-dialog.component';
import { EditPrinterDialogComponent } from './edit-printer-dialog/edit-printer-dialog.component';
import { PrinterTableDataSource } from './printer-table-datasource';
import { ViewPrinterDialogComponent } from './view-printer-dialog/view-printer-dialog.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-printer-table',
  templateUrl: './printer-table.component.html',
  standalone: true,
  imports: [
    MatCardModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule
  ],
  styleUrls: ['./printer-table.component.css']
})
export class PrinterTableComponent implements AfterViewInit, OnInit, OnChanges {

  @Input()
  printerClass: PrinterClass;
  printer: Printer;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatTable) table: MatTable<Printer>;
  dataSource: PrinterTableDataSource;

  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = ['name', 'deviceURI', 'description', 'location', 'state', 'isAcceptingJobs', 'actions'];
  activePrinterStates = ['IDLE', 'PROCESSING'];

  constructor(
    private printerService: PrinterService,
    private printerClassService: PrinterClassService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private translate: TranslateService) { }

  ngOnInit() {
    this.dataSource = new PrinterTableDataSource(this.printerService);
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.printerClass != null && changes.printerClass.currentValue != null) {
      console.log(changes.printerClass.currentValue);
      this.refreshSelectedPrinterClass(changes.printerClass.currentValue);
    }
  }

  /*******************************************************************
   *
   * PUBLIC API
   *
   ******************************************************************/

  openViewPrinterDialog(printr: Printer): void {
    const dialogRef = this.dialog.open(
      ViewPrinterDialogComponent,
      {
        minWidth: '600px',
        data: {
          printer: printr,
          classMembers: this.getOtherPrinterClassMemberNames(printr.name)
        }
      });
  }

  openAddPrinterDialog(): void {
    const dialogRef = this.dialog.open(
      AddPrinterDialogComponent,
      { minWidth: '350px', data: { printer: this.printer || { name: '', deviceURI: '', description: '', location: '' } } });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`AddPrinterDialog result: ${result}`);
      if (result) {
        console.log('Sending result ' + result.name + ' to backend!');
        console.log(result);
        result.deviceURI = 'socket://' + result.deviceURI + ':9100';
        this.printerService.createPrinter(result).subscribe(
          (p) => {
            console.log('SUCCESSFULLY created ' + p.name);
            // add new printer to printerclass
            if (this.printerClass.printerClassMembers == null) {
              this.printerClass.printerClassMembers = [p.printerURL.toString()];
            } else {
              this.printerClass.printerClassMembers.push(p.printerURL.toString());
            }
            this.printerClassService.createPrinterClass(this.printerClass).subscribe(
              (c) => {
                console.log('Printer added ' + p.name + ' to PrinterClass ' + c.printerClassName);
                const message = this.getTranslatedMessage(p, c);
                this.openSnackBar(message, 'OK', 3000);
                this.refreshSelectedPrinterClass(c);
              },
              (err) => this.handleError(err)
            );
          },
          (err) => this.handleError(err)
        );
      }
    });
  }

  openDeletePrinterDialog(printer: Printer): void {
    const dialogRef = this.dialog.open(
      DeletePrinterDialogComponent,
      { minWidth: '450px', data: printer });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`DeletePrinterDialog result: ${result}`);
      if (result) {
        this.printerService.deletePrinter(result).subscribe(
          (p) => {
            console.log('Drucker erfolgreich gelöscht');
            const message = this.getTranslatedDialogMessage('printerTableMessage.deletePrinterDialog');
            this.openSnackBar(message, 'OK', 3000);
            this.refreshSelectedPrinterClass(this.printerClass);
          },
          (err) => this.handleError(err)
        );
      }
    });
  }

  openEditPrinterDialog(printer: Printer): void {
    console.log(printer);
    const dialogRef = this.dialog.open(
      EditPrinterDialogComponent,
      { minWidth: '450px', data: {...printer} });
    dialogRef.afterClosed().subscribe(result => {
      console.log(`EditPrinterDialog result: ${result}`);
      if (result) {
        this.printerService.createPrinter(result).subscribe(
          (p) => {
            console.log('Drucker erfolgreich editiert');
            const message = this.getTranslatedDialogMessage('printerTableMessage.editPrinterDialog');
            this.openSnackBar(message, 'OK', 3000);
            this.refreshSelectedPrinterClass(this.printerClass);
          },
          (err) => this.handleError(err)
        );
      }
    });
  }

  pausePrinter(printerName: string) {
    this.printerService.pausePrinter(printerName)
      .subscribe(
        () => {
          console.log('Pause Printer Request send successfully!');
          const message = this.getTranslatedPrinterDialogMessage('printerTableMessage.pausePrinterDialogPrefix', 'printerTableMessage.pausePrinterDialogSuffix', printerName);
          this.openSnackBar(message, 'OK', 3000);
          this.refreshSelectedPrinterClass(this.printerClass);
        },
        (err) => this.handleError(err)
      );
  }

  resumePrinter(printerName: string) {
    this.printerService.resumePrinter(printerName)
      .subscribe(
        () => {
          console.log('Resume Printer Request send successfully!');
          const message = this.getTranslatedPrinterDialogMessage('printerTableMessage.resumePrinterDialogPrefix', 'printerTableMessage.resumePrinterDialogSuffix', printerName);
          this.openSnackBar(message, 'OK', 3000);
          this.refreshSelectedPrinterClass(this.printerClass);
        },
        (err) => this.handleError(err)
      );
  }

  disablePrinter(printerName: string) {
    this.printerService.disablePrinter(printerName)
      .subscribe(
        () => {
          console.log('Disable Printer Request send successfully!');
          const message = this.getTranslatedPrinterDialogMessage('printerTableMessage.disablePrinterDialogPrefix', 'printerTableMessage.disablePrinterDialogSuffix', printerName);
          this.openSnackBar(message, 'OK', 3000);
          this.refreshSelectedPrinterClass(this.printerClass);
        },
        (err) => this.handleError(err)
      );
  }

  enablePrinter(printerName: string) {
    this.printerService.enablePrinter(printerName)
      .subscribe(
        () => {
          console.log('Disable Printer Request send successfully!');
          const message = this.getTranslatedPrinterDialogMessage('printerTableMessage.enablePrinterDialogPrefix', 'printerTableMessage.enablePrinterDialogSuffix', printerName);
          this.openSnackBar(message, 'OK', 3000);
          this.refreshSelectedPrinterClass(this.printerClass);
        },
        (err) => this.handleError(err)
      );
  }

  printTestPage(printerName: string) {
    this.printerService.printTestPage(printerName)
      .subscribe(
        () => {
          console.log('Testpage Request send for ' + printerName);
          const message = this.getTranslatedPrinterDialogMessage('printerTableMessage.printTestPagePrefix', 'printerTableMessage.printTestPageSuffix', printerName);
          this.openSnackBar(message, 'OK', 3000);
          this.refreshSelectedPrinterClass(this.printerClass);
        },
        (err) => this.handleError(err)
      );
  }

  /*******************************************************************
   *
   * PRIVATE METHODS
   *
   ******************************************************************/

  private getPrinterNames(printerNames: string[]): string[] {
    if (printerNames == null) { return []; }
    return printerNames.map(
      p => p.split('/').pop()
    );
  }

  private refreshSelectedPrinterClass(printerClass: PrinterClass) {
    console.log('Refreshing PrinterClass ' + printerClass.printerClassName);
    this.dataSource.getPrintersBy(this.getPrinterNames(this.printerClass.printerClassMembers));
  }

  private openSnackBar(message: string, action: string, duration: number) {
    this.snackBar.open(message, action, {
      duration,
    });
  }

  private handleError(error: any) {
    console.log(error);
    if (error instanceof HttpErrorResponse) {
      this.openSnackBar(error.message, 'OK', 10000);
    }
  }

  /**
   * Filters the Name of the selected Printer out from the PrinterClassMembers
   */
  private getOtherPrinterClassMemberNames(printerName: string): string[] {
    return this.getPrinterNames(this.printerClass.printerClassMembers)
      .filter(p => printerName !== p);
  }

  private getTranslatedMessage(p: Printer, c: PrinterClass) {
    let messagePrefix;
    let messageMid;
    let messageSuffix;
    this.translate.get('printerTableMessage.addPrinterDialogPrefix').subscribe(
      (text) => messagePrefix = text,
      (err) => console.error(err)
    );
    this.translate.get('printerTableMessage.addPrinterDialogMid').subscribe(
      (text) => messageMid = text,
      (err) => console.error(err)
    );
    this.translate.get('printerTableMessage.addPrinterDialogMid').subscribe(
      (text) => messageMid = text,
      (err) => console.error(err)
    );
    this.translate.get('printerTableMessage.addPrinterDialogSuffix').subscribe(
      (text) => messageSuffix = text,
      (err) => console.error(err)
    );
    const message = messagePrefix + ' ' + p.name + ' ' + messageMid + ' ' + c.printerClassName + ' ' + messageSuffix;
    return message;
  }

  private getTranslatedDialogMessage(key: string): string {
    let message;
    this.translate.get(key).subscribe(
      (text) => message = text,
      (err) => console.error(err)
    );
    return message;
  }

  private getTranslatedPrinterDialogMessage(prefixKey: string, suffixKey: string, printerName: string): string {
    let prefix;
    let suffix;
    this.translate.get(prefixKey).subscribe(
      (v) => prefix = v,
      (err) => console.error(err)
    );
    this.translate.get(suffixKey).subscribe(
      (v) => suffix = v,
      (err) => console.error(err)
    );
    return prefix + ' ' + printerName + ' ' + suffix;
  }
}
